import { useRef } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { ImageIcon, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface UploadedImage {
	url: string;
	name: string;
	width: number;
	height: number;
	size: number;
	/** The picked File, present only for a freshly-selected image (absent for an existing server URL). */
	file?: File;
}

interface ImageUploaderProps {
	value: UploadedImage | null;
	onChange: (next: UploadedImage | null) => void;
	accept?: string;
	maxSize?: number;
	/** Override the 80×80 preview (e.g. render a policy badge gradient instead). */
	renderPreview?: (image: UploadedImage | null) => ReactNode;
	helpText?: string;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp';
const DEFAULT_MAX_SIZE = 2 * 1024 * 1024;

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 80×80 preview + metadata + actions. Reads the file client-side into an object URL. */
export function ImageUploader({
	value,
	onChange,
	accept = DEFAULT_ACCEPT,
	maxSize = DEFAULT_MAX_SIZE,
	renderPreview,
	helpText
}: ImageUploaderProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || file.size > maxSize) return;
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			onChange({ url, name: file.name, width: img.naturalWidth, height: img.naturalHeight, size: file.size, file });
		};
		img.src = url;
	};

	const preview = renderPreview ? (
		renderPreview(value)
	) : value ? (
		<img src={value.url} alt={value.name} className="h-20 w-20 rounded-md object-cover" />
	) : (
		<div className="flex h-20 w-20 items-center justify-center rounded-md border-2 border-dashed border-border-strong">
			<ImageIcon size={24} className="text-foreground-disabled" />
		</div>
	);

	return (
		<div className="flex items-start gap-4">
			{preview}
			<div className="flex-1">
				{value ? (
					<>
						<p className="truncate text-sm font-medium text-foreground">{value.name}</p>
						<p className="text-xs text-foreground-muted">
							{value.width}×{value.height} · {formatBytes(value.size)}
						</p>
						<div className="mt-2 flex gap-2">
							<Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
								Αντικατάσταση
							</Button>
							<Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
								Αφαίρεση
							</Button>
						</div>
					</>
				) : (
					<>
						<p className="text-sm text-foreground-subtle">Καμία εικόνα</p>
						{helpText && <p className="mt-0.5 text-xs text-foreground-muted">{helpText}</p>}
						<Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()}>
							<Upload size={14} /> Μεταφόρτωση εικόνας
						</Button>
					</>
				)}
				<input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleFile} />
			</div>
		</div>
	);
}
